import { useEffect } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { fieldLabels } from '@/features/consignment-form/labels'
import { POSTCODE_LENGTH } from '@/features/consignment-form/rules'
import type { Consignment } from '@/features/consignment-form/schema'
import { t } from '@/lang'
import { FormField } from '@/ui/FormField'
import { Input } from '@/ui/Input'

type Props = {
  form: UseFormReturn<Consignment>
  party: 'shipper' | 'consignee'
}

/** Legal-entity details, shown for the shipper and for the consignee alike. */
export function CompanyFieldset({ form, party }: Props) {
  const { clearErrors, register, formState } = form
  const errors = formState.errors[party]

  useEffect(
    () => () => clearErrors([`${party}.companyName`, `${party}.street`, `${party}.postcode`, `${party}.locality`]),
    [clearErrors, party]
  )

  return (
    <div className="mt-2 space-y-2 md:mt-5 md:space-y-5">
      <div className="grid gap-5 md:grid-cols-3 md:gap-7.5">
        <FormField
          htmlFor={`${party}-company-name`}
          label={fieldLabels[`${party}.companyName`]}
          error={errors?.companyName?.message}
          required
        >
          <Input
            id={`${party}-company-name`}
            placeholder={t('company.name-placeholder')}
            {...register(`${party}.companyName`)}
          />
        </FormField>
      </div>
      <fieldset>
        <legend className="text-label text-neutral-800">{t('company.address-legend')}</legend>
        <div className="mt-1 grid gap-x-5 gap-y-2 md:mt-1.5 md:grid-cols-3 md:gap-7.5">
          <FormField
            htmlFor={`${party}-street`}
            label={fieldLabels[`${party}.street`]}
            error={errors?.street?.message}
            required
          >
            <Input
              id={`${party}-street`}
              placeholder={t('company.street-placeholder')}
              autoComplete="street-address"
              {...register(`${party}.street`)}
            />
          </FormField>
          <FormField
            htmlFor={`${party}-postcode`}
            label={fieldLabels[`${party}.postcode`]}
            error={errors?.postcode?.message}
            required
          >
            <Input
              id={`${party}-postcode`}
              placeholder={t('company.postcode-placeholder')}
              autoComplete="postal-code"
              autoCapitalize="characters"
              maxLength={POSTCODE_LENGTH}
              className="uppercase"
              {...register(`${party}.postcode`)}
            />
          </FormField>
          <FormField
            htmlFor={`${party}-locality`}
            label={fieldLabels[`${party}.locality`]}
            error={errors?.locality?.message}
            required
          >
            <Input
              id={`${party}-locality`}
              placeholder={t('company.locality-placeholder')}
              autoComplete="address-level2"
              {...register(`${party}.locality`)}
            />
          </FormField>
        </div>
      </fieldset>
    </div>
  )
}
